import type { ProjectListItem } from '@greco-gls/shared';

/**
 * The numbers behind the dashboard: how much of each project is translated, and the colour a
 * coverage cell gets.
 *
 * "Translated" leaves the source locale out. Its coverage is how many keys have a source text at
 * all, which is a different question from how much is left to translate, and folding it in would
 * lift every project's figure towards 100 %.
 */

export interface Units {
  translated: number;
  total: number;
}

/**
 * Whole percent, never rounded up to 100: a locale with one value missing must not look done.
 * `null` when there is nothing to translate.
 */
export function percentOf({ translated, total }: Units): number | null {
  if (!total) return null;
  if (translated >= total) return 100;
  return Math.min(99, Math.floor((translated / total) * 100));
}

/** Target locales only — see the note at the top. */
export function targetUnits(item: ProjectListItem): Units {
  return item.coverage
    .filter((entry) => entry.locale !== item.project.sourceLocale)
    .reduce<Units>(
      (sum, entry) => ({
        translated: sum.translated + Math.min(entry.translated, entry.total),
        total: sum.total + entry.total,
      }),
      { translated: 0, total: 0 },
    );
}

export function sumUnits(items: ProjectListItem[]): Units {
  return items.map(targetUnits).reduce<Units>(
    (sum, units) => ({
      translated: sum.translated + units.translated,
      total: sum.total + units.total,
    }),
    { translated: 0, total: 0 },
  );
}

/**
 * Every locale any of the projects uses, the most widely used first, so the columns most rows
 * fill sit next to the project names and the rare ones trail off to the right.
 */
export function localeColumns(items: ProjectListItem[]): string[] {
  const uses = new Map<string, number>();
  for (const item of items) {
    for (const entry of item.coverage) uses.set(entry.locale, (uses.get(entry.locale) ?? 0) + 1);
  }
  return [...uses.entries()]
    .sort(([a, aUses], [b, bUses]) => bUses - aUses || a.localeCompare(b))
    .map(([locale]) => locale);
}

export type SortOrder = 'least-covered' | 'name' | 'most-keys';

export const SORT_LABELS: Record<SortOrder, string> = {
  'least-covered': 'Least covered first',
  name: 'Name',
  'most-keys': 'Most keys first',
};

/** A project with nothing to translate has no percentage; it sorts after every one that does. */
export function sortProjects(items: ProjectListItem[], order: SortOrder): ProjectListItem[] {
  const byName = (a: ProjectListItem, b: ProjectListItem) =>
    a.project.name.localeCompare(b.project.name);
  const sorted = [...items];
  switch (order) {
    case 'name':
      return sorted.sort(byName);
    case 'most-keys':
      return sorted.sort((a, b) => b.keyCount - a.keyCount || byName(a, b));
    case 'least-covered':
      return sorted.sort((a, b) => {
        const left = percentOf(targetUnits(a)) ?? 101;
        const right = percentOf(targetUnits(b)) ?? 101;
        return left - right || byName(a, b);
      });
  }
}

/** True while any target locale still has a value to write. */
export function hasGaps(item: ProjectListItem): boolean {
  const units = targetUnits(item);
  return units.translated < units.total;
}

export function matchesSearch(item: ProjectListItem, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    item.project.code.toLowerCase().includes(needle) ||
    item.project.name.toLowerCase().includes(needle)
  );
}

/**
 * Five steps on one hue (dataviz: sequential = one hue, light to dark). The colours live in the
 * theme (`palette.coverage[level]`), each with a text colour of at least 4.5:1 against its fill.
 * The percentage is always printed in the cell, so the colour is never the only signal.
 */
export const COVERAGE_STEPS = [
  { level: 0, min: 0, label: 'under 50 %' },
  { level: 1, min: 50, label: '50–79 %' },
  { level: 2, min: 80, label: '80–94 %' },
  { level: 3, min: 95, label: '95–99 %' },
  { level: 4, min: 100, label: '100 %' },
] as const;

export type CoverageStep = (typeof COVERAGE_STEPS)[number];

export function coverageStep(percent: number): CoverageStep {
  let step: CoverageStep = COVERAGE_STEPS[0];
  for (const candidate of COVERAGE_STEPS) if (percent >= candidate.min) step = candidate;
  return step;
}

export const formatCount = (value: number) => new Intl.NumberFormat('en-GB').format(value);
